import { Badge, Box, Checkbox, Divider, Flex, Text } from '@chakra-ui/react'
import { useProfile } from '@contexts/user/ProfileContext'
import { Assessment, assessmentPlanField, getCorrectedValue } from '@hb/shared'
import { useCallback, useEffect, useMemo } from 'react'
import { useField, useForm } from 'react-final-form'
import { FormElement } from '../../../../components/forms/Input'
import { SectionLabel } from '../../SectionLabel'
import { ProblemListInput } from './ProblemListInput'

// const MS_PER_DAY = 86_400_000
const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000

const formatGa = (totalDays: number) => {
  const weeks = Math.floor(totalDays / 7)
  const days = totalDays % 7
  return `${weeks}w ${days}d`
}

const formatEdd = (eddMs: number) => {
  const d = new Date(eddMs)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const GAConfirmationRow = () => {
  const {
    input: { value: gestationalAgeDays },
  } = useField<number | undefined>('data.gestationalAge', { subscription: { value: true } })
  const {
    input: { value: gaConfirmed, onChange },
  } = useField<boolean | undefined>('data.gaConfirmed', { subscription: { value: true } })

  const { selectedAssessment } = useProfile()
  const edd = useMemo(
    () =>
      getCorrectedValue(selectedAssessment.populated as Assessment | null, 'delivery.edd') as
        | number
        | undefined,
    [selectedAssessment.populated],
  )

  const gaText = gestationalAgeDays != null ? formatGa(gestationalAgeDays) : null
  const eddText = edd != null ? formatEdd(edd) : null

  return (
    <Flex align="center" gap={3} px={1} flexWrap="wrap">
      <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }} fontWeight={600}>
        Gestational Age:
      </Text>
      {gaText ? (
        <Badge colorScheme="sage" fontSize="xs" borderRadius="full" px={3} py={0.5}>
          {gaText}
        </Badge>
      ) : (
        <Text fontSize="sm" color="gray.400" fontStyle="italic">
          Not available
        </Text>
      )}
      {eddText && (
        <Text fontSize="xs" color="gray.500" _dark={{ color: 'gray.400' }}>
          EDD: {eddText}
        </Text>
      )}
      <Box px={1} data-child-name="data.gaConfirmed">
        <Checkbox
          isChecked={!!gaConfirmed}
          onChange={e => onChange(e.target.checked)}
          colorScheme="green"
          size="sm">
          <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }}>
            Confirmed
          </Text>
        </Checkbox>
      </Box>
    </Flex>
  )
}

const useAssessmentTemplate = () => {
  const { assessments, assessmentId, user } = useProfile()
  const {
    input: { value: gestationalAgeDays },
  } = useField<number | undefined>('data.gestationalAge', { subscription: { value: true } })
  const {
    input: { value: visitType },
  } = useField<string | undefined>('config.visitType', { subscription: { value: true } })
  const {
    input: { value: currentAssessment },
  } = useField<string | undefined>('data.assessment', { subscription: { value: true } })
  const form = useForm()

  const { gravida, para } = useMemo(() => {
    const pastAssessments = Object.entries(assessments ?? {})
      .filter(([id]) => id !== assessmentId)
      .map(([, a]) => a)
    const additionalPregnancies =
      (user as any)?.gyn?.mergedData?.gynAndSexualHistory?.gynHistory?.additionalPregnancies ?? []
    let totalPast = 0
    let paraCount = 0
    for (const a of pastAssessments) {
      totalPast++
      const outcome = (a.corrections?.pregnancyOutcome ?? a.data?.pregnancyOutcome) as
        | string
        | undefined
      if (outcome !== 'miscarriage' && outcome !== 'abortion') paraCount++
    }
    for (const p of additionalPregnancies) {
      totalPast++
      const outcome = p.pregnancyOutcome as string | undefined
      if (
        outcome === 'born-living' ||
        outcome === 'born-with-complications' ||
        outcome === 'stillborn'
      )
        paraCount++
    }
    return { gravida: totalPast + 1, para: paraCount }
  }, [assessments, assessmentId, user])

  const template = useMemo(() => {
    const gaStr = gestationalAgeDays != null ? formatGa(gestationalAgeDays) : null
    const gpStr = `G${gravida}P${para}`
    const visitTypeStr = visitType === 'non-routine' ? 'non-routine' : 'routine'
    const dob = (user as any)?.dob as number | undefined
    const agePart = dob ? `${Math.floor((Date.now() - dob) / MS_PER_YEAR)}-year-old ` : ''
    const gaPart = gaStr ? ` at ${gaStr}` : ''
    return `${agePart}${gpStr}${gaPart}, presenting for ${visitTypeStr} antepartum visit.`
  }, [gestationalAgeDays, gravida, para, visitType, user])

  const setTemplate = useCallback(() => {
    if (!currentAssessment && template) {
      form.change('data.assessment', template)
    }
  }, [currentAssessment, template, form])

  return setTemplate
}

export const AssessmentPlanInput = () => {
  const setTemplate = useAssessmentTemplate()

  useEffect(() => {
    setTemplate()
  }, [setTemplate])

  return (
    <Box data-child-name="data" w="100%" px={4} py={3}>
      <SectionLabel>Assessment &amp; Plan</SectionLabel>
      <GAConfirmationRow />
      <Divider my={3} />
      <ProblemListInput />
      <Divider my={3} />
      <FormElement root showName={false} field={assessmentPlanField} name="data" />
    </Box>
  )
}
